import type { ChannelNguoiChia } from '@qlk/shared';
import {
  CHANNEL_SORT_LABEL,
  CACH_SAP_KHONG_TIEN,
  cachSapXemDuoc,
  CHANNEL_TABS,
  CHANNEL_TAB_LABEL,
  MONETIZATION_LABEL,
  MONETIZATION_STATUSES,
  VAI_TRO_CHIA_LABEL,
  nhanKhoangNgay,
  type ChannelRow,
  type ChannelSort,
  type ChannelTabId,
  type VaiTroChia,
} from '@qlk/shared';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useKyChung } from '../../../layout/kyChung.js';
import { nhanSoKyTruoc } from '../../../lib/kyTrang.js';
import { coQuyen, usePhamViDuLieu, useToi, useXemTien } from '../../../lib/auth.js';
import {
  CHUA_DU_LUOT_XEM,
  ChuThichChuaDuDuLieu,
  DataTable,
  dinhDangTien,
  EmptyState,
  KhoiChuaCoKenh,
  MenuThaoTac,
  Money,
  Nut,
  OChon,
  OTimKiem,
  PageHeader,
  SegmentedControl,
  StatStrip,
  ToolbarBang,
  type CotBang,
  type OChiSo,
} from '../../../ui/index.js';
import { useDoiTac, useTomTatNetwork } from '../../networks/api.js';
import { useDongBoNgay, useTomTatDongBo } from '../../sync/api.js';
import { useNhom } from '../../team/api.js';
import { useDanhSachKenh, useTomTatKenh } from '../api.js';
import { BieuDoTyTrong, TheKenhChuaCoDoanhThu } from '../components/BieuDoTyTrong.js';
import { BadgeKiemTien, OKenh } from '../components/OKenh.js';
import { ONguonTien } from '../components/ONguonTien.js';
import { gonSo, phanTram } from '../components/soLieu.js';

/**
 * Trang Quản lý kênh — bước 2/3 của nâng cấp giao diện.
 *
 * Ba thứ đổi, mỗi thứ vì một lý do:
 *
 * - **Kỳ số liệu lấy từ ô chung** ở thanh trên (`?ky=`), bộ chọn riêng trong
 *   trang đã bỏ. Hai ô cùng hỏi "đang xem kỳ nào" là lỗi gặp ngay.
 * - **Tab thành `SegmentedControl` và nằm trên URL** (`?tab=`): nó lọc một
 *   bảng, không đổi nội dung cả trang — và dán link phải ra đúng tập kênh.
 * - **Bảng còn 10 cột**. "Chủ sở hữu chính" và "Sức khỏe" thành icon cạnh tên
 *   (xem `OKenh`), "Phụ trách" xem trong trang kênh. Các cột chia tiền chuyển
 *   sang hai cách xem "Theo nhóm" / "Theo người" — nơi chúng có tổng con để so.
 */

/** Ba cách xem bảng kênh. "Theo nhóm" và "Theo người" gom dòng rồi cộng tổng con. */
const CACH_XEM = [
  { value: 'danh-sach', label: 'Danh sách' },
  { value: 'theo-nhom', label: 'Theo nhóm' },
  { value: 'theo-nguoi', label: 'Theo người' },
] as const;

type CachXem = (typeof CACH_XEM)[number]['value'];

/** Khoá nhóm giả cho kênh chưa gán nhóm — luôn hiện, kể cả khi rỗng. */
const KHONG_NHOM = 'khong-nhom';

/**
 * Ô "Phần của bạn" / "Chia nhân sự" / "Công ty giữ lại" — `null` hiện `—`.
 *
 * `null` ở `phanCuaToi` nghĩa là **không hưởng %** ở kênh đó; `0,00 US$` ở đây
 * đọc ra là "được chia nhưng ra không đồng", nên nó phải là một gạch ngang.
 */
function OTienChia({ giaTri, am }: { giaTri: number | null | undefined; am?: boolean }) {
  if (giaTri === null || giaTri === undefined) return <span className="text-chu-mo">—</span>;
  return <Money value={giaTri} className={am === true && giaTri < 0 ? 'text-giam' : ''} />;
}

/** Pill "tên · %" của một vai trò trong bảng. */
/**
 * Pill "tên · %" của một vai trò.
 *
 * `pct`, `coDinhUsd` và `tien` là **tuỳ chọn**: máy chủ bỏ hẳn chúng với người
 * không có `revenue:view` (mục S5). Lúc đó pill còn đúng cái tên — đội ngũ kênh
 * không phải một con số tiền, nên vẫn hiện.
 */
function PillNguoi({ ds }: { ds: ChannelNguoiChia[] }) {
  if (ds.length === 0) return <span className="text-chu-phu">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {ds.map((n) => (
        <span
          key={n.userId}
          className="inline-flex items-center rounded-chip bg-neutral-nen px-2 py-0.5 text-phu text-neutral-chu"
          {...(n.tien === undefined
            ? {}
            : { title: `${n.tenNguoi}: ${dinhDangTien(n.tien)} kỳ này` })}
        >
          {n.tenNguoi}
          {(n.pct ?? 0) > 0 && (
            <span className="so-lieu ml-1">
              · {(n.pct ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%
            </span>
          )}
          {(n.pct ?? 0) === 0 && (n.coDinhUsd ?? 0) > 0 && (
            <span className="so-lieu ml-1">· {dinhDangTien(n.coDinhUsd ?? 0)}/th</span>
          )}
        </span>
      ))}
    </div>
  );
}

export function TrangDanhSachKenh() {
  const toi = useToi();
  const dieuHuong = useNavigate();
  const coSua = coQuyen(toi.data?.permissions, 'channels', 'edit');
  const coXemNetwork = coQuyen(toi.data?.permissions, 'networks', 'view');
  /*
   * `GET /networks/summary` là thẻ tổng của cả công ty nên trả 403 cho người bị
   * giới hạn theo kênh được giao (mục V4a). Ở đây nó chỉ cho một dòng phụ nên
   * không có khung đỏ nào, nhưng gọi vào vẫn là một 403 vô ích mỗi lần nạp
   * trang.
   */
  const { biGioiHanKenh, daBiet } = usePhamViDuLieu();
  const coDongBo = coQuyen(toi.data?.permissions, 'sync', 'edit');
  const coXemSync = coQuyen(toi.data?.permissions, 'sync', 'view');

  /* Kỳ số liệu: ô chọn chung ở thanh trên (`layout/kyChung`), kiểu `moc`. */
  const ky = useKyChung();
  /* Kỳ ngoài mốc dữ liệu: dải chỉ số hiện `—` thay vì số 0 của API. */
  const kyRong = ky.kyRong;
  const kyTrang = ky.ky;
  const period = kyTrang?.ma ?? ky.giaTri;
  const nhanKy = kyTrang?.nhan ?? '';

  /*
   * Tab nằm trên URL: "gửi cho tôi xem mấy kênh chưa kiếm tiền" phải dán được
   * bằng một đường dẫn. Khối "Kênh theo nguồn tiền" ở Tổng quan cũng link vào
   * đây bằng `?tab=`.
   */
  const [thamSo, datThamSo] = useSearchParams();
  const thamSoTab = thamSo.get('tab') ?? '';
  const tab: ChannelTabId = (CHANNEL_TABS as readonly string[]).includes(thamSoTab)
    ? (thamSoTab as ChannelTabId)
    : 'all';

  const datTab = (giaTri: string) => {
    const tiep = new URLSearchParams(thamSo);
    if (giaTri === 'all') tiep.delete('tab');
    else tiep.set('tab', giaTri);
    datThamSo(tiep);
  };

  const [q, setQ] = useState('');
  const [partner, setPartner] = useState('');
  /*
   * Rà soát bảo mật 31/10/2026, mục **S5**: thiếu `revenue:view` thì máy chủ bỏ
   * hẳn mọi trường tiền, nên trang phải ẩn cột, ẩn ô chỉ số và ẩn lựa chọn sắp
   * theo tiền. Mặc định đổi từ `revenue` sang `views` — con số họ làm việc với.
   */
  const { coXemTien } = useXemTien();
  const [sort, setSort] = useState<ChannelSort>(coXemTien ? 'revenue' : CACH_SAP_KHONG_TIEN);
  /*
   * Tìm theo email có quyền trên Tài khoản thương hiệu. Tách khỏi ô "Tìm kênh"
   * vì hai việc khác nhau: ô kia tìm trong tên/handle/chủ đề, ô này tìm trong
   * danh sách chủ sở hữu và người quản lý — dùng khi nhân sự nghỉ việc.
   */
  const [emailQuyen, datEmailQuyen] = useState('');
  const [kiemTien, setKiemTien] = useState('');
  const [cachXem, setCachXem] = useState<CachXem>('danh-sach');
  const [nhomLoc, setNhomLoc] = useState('');
  const [nguoiLoc, setNguoiLoc] = useState('');
  const [vaiTroLoc, setVaiTroLoc] = useState('');
  const [moLocThem, setMoLocThem] = useState(false);

  const tomTat = useTomTatKenh(period);
  // Số network đang trả tiền do module 02 sở hữu (hàm `laDoiTacDangTraTien`),
  // không đếm lại ở đây — xem 12.0 mục A3.
  const tomTatNetwork = useTomTatNetwork(coXemNetwork && daBiet && !biGioiHanKenh);
  // Danh sách đối tác và tóm tắt kết nối đều do module chủ cung cấp (bước 12.1).
  const danhSachDoiTac = useDoiTac(undefined, coXemNetwork);
  const tomTatSync = useTomTatDongBo(coXemSync || coDongBo);
  const dongBoNgay = useDongBoNgay();
  const danhSach = useDanhSachKenh({
    period,
    tab,
    sort,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(partner ? { paymentPartnerId: partner } : {}),
    ...(emailQuyen.trim() ? { emailQuyen: emailQuyen.trim().toLowerCase() } : {}),
    ...(kiemTien ? { monetizationStatus: kiemTien } : {}),
    ...(nhomLoc ? { teamId: nhomLoc } : {}),
    ...(nguoiLoc ? { nguoiId: nguoiLoc } : {}),
    ...(nguoiLoc && vaiTroLoc ? { vaiTroChia: vaiTroLoc as VaiTroChia } : {}),
  });

  /*
   * Danh sách nhóm cho ô lọc. Lấy của module 10 ở kỳ hiện tại; thiếu quyền thì
   * truy vấn hỏng và ô lọc gom tạm từ dữ liệu đang hiển thị.
   */
  const kyNay = new Date().toISOString().slice(0, 7);
  const nhomDaCo = useNhom(kyNay, coQuyen(toi.data?.permissions, 'team', 'view'));

  const dongs = useMemo(
    () => danhSach.data?.pages.flatMap((t) => t.items) ?? [],
    [danhSach.data?.pages],
  );
  const tong = danhSach.data?.pages[0]?.totals;

  /*
   * Người đang xem có thấy phần chia **đầy đủ** hay không — đọc từ **máy chủ**
   * (`chiaSe.daChe`), không tự suy từ ma trận quyền ở giao diện.
   *
   * Luật thật nằm ở `api/lib/chiaDayDu.duocXemChiaDayDu` (team:admin **và**
   * phạm vi toàn công ty). Suy lại ở đây là có hai luật, và cái ở giao diện sẽ
   * là cái sai. Bảng rỗng thì không có dòng nào để đọc; khi đó đoán theo quyền
   * cũng không hiện số nào nên vô hại.
   */
  const xemChiaDayDu =
    dongs.find((d) => d.chiaSe)?.chiaSe?.daChe === false ||
    (dongs.every((d) => !d.chiaSe) &&
      coQuyen(toi.data?.permissions, 'team', 'admin') &&
      daBiet &&
      !biGioiHanKenh);

  // Danh sách Network lấy từ `GET /api/v1/networks` của module 02. Thiếu quyền
  // `networks:view` thì gom tạm từ dữ liệu đang hiển thị để ô lọc vẫn dùng được.
  const doiTac = useMemo(() => {
    if (danhSachDoiTac.data) {
      return danhSachDoiTac.data.map((d) => ({ value: d.id, label: d.name }));
    }
    const map = new Map<string, string>();
    for (const d of dongs) if (d.payment) map.set(d.payment.partnerId, d.payment.partnerName);
    return [...map].map(([value, label]) => ({ value, label }));
  }, [danhSachDoiTac.data, dongs]);

  const nhomChon = useMemo(() => {
    if (nhomDaCo.data) return nhomDaCo.data.map((n) => ({ value: n.id, label: n.name }));
    const map = new Map<string, string>();
    for (const d of dongs) {
      if (d.chiaSe?.teamId) map.set(d.chiaSe.teamId, d.chiaSe.tenNhom ?? d.chiaSe.teamId);
    }
    return [...map].map(([value, label]) => ({ value, label }));
  }, [nhomDaCo.data, dongs]);

  /* Người trong đội ngũ gom từ chính các dòng đang hiện — không gọi thêm API. */
  const nguoiChon = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dongs) {
      for (const n of [
        ...(d.chiaSe?.truongNhom ?? []),
        ...(d.chiaSe?.quanLy ?? []),
        ...(d.chiaSe?.editor ?? []),
      ]) {
        map.set(n.userId, n.tenNguoi);
      }
    }
    return [...map].map(([value, label]) => ({ value, label }));
  }, [dongs]);

  /** Cột "Kênh" — dùng ở cả ba cách xem. */
  const cotKenh: CotBang<ChannelRow> = {
    key: 'kenh',
    header: 'Kênh',
    ghim: true,
    width: '240px',
    render: (d) => <OKenh kenh={d} />,
  };

  const cotNhom: CotBang<ChannelRow> = {
    key: 'nhom',
    header: 'Nhóm',
    width: '130px',
    render: (d) => (
      /* Một dòng, cắt bằng `truncate`: tên nhóm hai ba chữ mà xuống dòng thì
         hàng cao gấp đôi chỉ vì một cột phụ. */
      <span
        className={`block truncate ${d.chiaSe?.tenNhom ? 'text-chu' : 'text-chu-phu'}`}
        title={d.chiaSe?.tenNhom ?? 'Chưa gán nhóm'}
      >
        {d.chiaSe?.tenNhom ?? 'Chưa gán nhóm'}
      </span>
    ),
  };

  /** Bảng mặc định: mười cột, mỗi cột trả lời một câu về kênh. */
  const cotDanhSach: CotBang<ChannelRow>[] = [
    cotKenh,
    {
      key: 'nguonTien',
      header: 'Nguồn tiền',
      width: '150px',
      render: (d) => <ONguonTien kenh={d} />,
    },
    cotNhom,
    {
      key: 'subs',
      header: 'Subs',
      align: 'right',
      render: (d) => <span className="so-lieu">{gonSo(d.metrics?.subscribers)}</span>,
    },
    {
      key: 'views',
      header: 'Xem',
      align: 'right',
      width: '104px',
      render: (d) => (
        <div className="flex flex-col items-end">
          <span className="so-lieu">{gonSo(d.metrics?.views)}</span>
          {/*
           * Đếm **ngày thật sự có lượt xem** (`soNgayCoViews`), không đếm tiến
           * độ lấy lịch sử: một ngày có thể đã có dòng (job doanh thu ghi) mà
           * chưa có lượt xem, và đó chính là chỗ cột này từng nói dối.
           */}
          <ChuThichChuaDuDuLieu
            soNgayDaCo={d.metrics?.soNgayCoViews}
            soNgayCuaKy={d.metrics?.soNgayTrongKy ?? kyTrang?.days ?? 0}
          />
        </div>
      ),
    },
    ...(!coXemTien
      ? []
      : ([
          {
            key: 'gop',
            header: 'Doanh thu gộp',
            align: 'right',
            render: (d) => <Money value={d.revenueSummary?.grossRevenue} />,
          },
          {
            key: 'thucNhan',
            header: 'Thực nhận',
            align: 'right',
            render: (d) =>
              d.revenueSummary ? (
                <div>
                  <Money value={d.revenueSummary.companyNet} className="font-semibold text-chu" />
                  {d.revenueSummary.deltaPercent !== null && (
                    <p
                      className={`so-lieu text-phu ${d.revenueSummary.deltaPercent >= 0 ? 'text-tang' : 'text-giam'}`}
                      /* Cột hẹp nên chỉ có phần trăm; khoảng đã so nằm ở tooltip. */
                      title={`${d.revenueSummary.deltaPercent >= 0 ? 'Tăng' : 'Giảm'} ${phanTram(
                        Math.abs(d.revenueSummary.deltaPercent),
                      )} ${nhanSoKyTruoc(kyTrang)}`}
                    >
                      {d.revenueSummary.deltaPercent >= 0 ? '▲' : '▼'}{' '}
                      {phanTram(Math.abs(d.revenueSummary.deltaPercent))}
                    </p>
                  )}
                </div>
              ) : null,
          },
          /*
           * Chuỗi tiền không dừng ở thực nhận. Hai cột cuối của phép trừ đứng **ngay
           * sau** nó, đúng thứ tự chốt 14/10/2026; người xem hẹp thấy một cột "Phần
           * của bạn" thay cho cả hai (máy chủ không gửi hai số kia).
           *
           * Trước bản này các cột chia chỉ có ở hai cách xem "Theo nhóm"/"Theo
           * người", nên bảng mặc định dừng ở thực nhận và không ai thấy được tiền
           * của mình ở danh sách.
           */
          ...(xemChiaDayDu
            ? ([
                {
                  key: 'chiaNhanSuDs',
                  header: 'Chia nhân sự',
                  align: 'right',
                  render: (d) => <OTienChia giaTri={d.chiaSe?.chiaNhanSu} />,
                },
                {
                  key: 'congTyGiuLaiDs',
                  header: 'Công ty giữ lại',
                  align: 'right',
                  render: (d) => <OTienChia giaTri={d.chiaSe?.congTyGiu} am />,
                },
              ] satisfies CotBang<ChannelRow>[])
            : ([
                {
                  key: 'phanCuaToiDs',
                  header: 'Phần của bạn',
                  align: 'right',
                  render: (d) => (
                    <div>
                      <OTienChia giaTri={d.chiaSe?.phanCuaToi} />
                      {(d.chiaSe?.vaiTroCuaToi ?? []).length > 0 && (
                        <p className="text-phu text-chu-phu">
                          {(d.chiaSe?.vaiTroCuaToi ?? [])
                            .map((v) => VAI_TRO_CHIA_LABEL[v])
                            .join(', ')}
                          {d.chiaSe?.pctCuaToi === null || d.chiaSe?.pctCuaToi === undefined
                            ? ''
                            : ` ${phanTram(d.chiaSe.pctCuaToi)}`}
                        </p>
                      )}
                    </div>
                  ),
                },
              ] satisfies CotBang<ChannelRow>[])),
          {
            key: 'rpm',
            header: 'RPM',
            align: 'right',
            /*
             * `rpm === null` = **chưa đủ số liệu lượt xem** (không ngày nào trong kỳ
             * có cả doanh thu và lượt xem > 0). Hiện `—` kèm giải thích, chứ không
             * hiện 0,00 $ và cũng không chia cho một mẫu số thiếu ngày.
             */
            render: (d) =>
              d.revenueSummary && d.revenueSummary.rpm === null ? (
                <span className="text-chu-mo" title={CHUA_DU_LUOT_XEM}>
                  —
                </span>
              ) : (
                <Money value={d.revenueSummary?.rpm} />
              ),
          },
        ] satisfies CotBang<ChannelRow>[])),
    {
      key: 'tinhTrang',
      header: 'Tình trạng',
      render: (d) => <BadgeKiemTien trangThai={d.monetizationStatus} />,
    },
    {
      key: 'thaoTac',
      header: '',
      width: '48px',
      render: (d) => (
        <div className="flex justify-end">
          <MenuThaoTac
            aria-label={`Thao tác với kênh ${d.name}`}
            muc={[
              {
                nhan: 'Mở trang kênh',
                onChon: () => {
                  void dieuHuong(`/kenh/${d.id}`);
                },
              },
              ...(coSua && d.archivedAt === null
                ? [
                    {
                      nhan: 'Sửa kênh',
                      onChon: () => {
                        void dieuHuong(`/kenh/${d.id}/sua`);
                      },
                    },
                  ]
                : []),
              {
                nhan: 'Doanh thu của kênh',
                onChon: () => {
                  void dieuHuong(`/kenh/${d.id}?tab=doanh-thu-chia-se`);
                },
              },
              ...(d.youtubeChannelId === null
                ? []
                : [
                    {
                      nhan: 'Mở trong YouTube Studio ↗',
                      onChon: () => {
                        window.open(
                          `https://studio.youtube.com/channel/${d.youtubeChannelId ?? ''}`,
                          '_blank',
                          'noreferrer',
                        );
                      },
                    },
                  ]),
            ]}
          />
        </div>
      ),
    },
  ];

  /**
   * Bảng của hai cách xem có tổng con: thay các cột số liệu YouTube bằng **các
   * cột chia tiền**. Tổng con chỉ có nghĩa với tiền — cộng RPM của năm kênh ra
   * một con số vô nghĩa.
   */
  const cotChiaTien: CotBang<ChannelRow>[] = [
    cotKenh,
    cotNhom,
    ...(!coXemTien
      ? []
      : ([
          {
            key: 'thucNhan',
            header: 'Thực nhận',
            align: 'right',
            render: (d) => (d.chiaSe ? <Money value={d.chiaSe.thucNhan} /> : null),
          },
          {
            key: 'chiPhi',
            header: 'Chi phí',
            align: 'right',
            render: (d) => (
              <div>
                <Money value={d.chiaSe?.chiPhi ?? 0} />
                {/*
            Chưa đặt ngày bắt đầu vận hành thì chi phí đang bị trừ **toàn bộ**
            (xem `chiPhiTinhVaoVanHanh`). Nhãn ở đúng cột chi phí, vì đó là con
            số bị ảnh hưởng.
          */}
                {d.chiaSe?.thieuNgayVanHanh === true && (
                  <p className="text-phu text-warn-chu">Chưa đặt ngày vận hành</p>
                )}
              </div>
            ),
          },
          {
            key: 'loiNhuan',
            header: 'Lợi nhuận',
            align: 'right',
            render: (d) =>
              d.chiaSe ? (
                <div>
                  <Money
                    value={d.chiaSe.loiNhuan}
                    className={(d.chiaSe.loiNhuan ?? 0) < 0 ? 'text-giam' : ''}
                  />
                  {d.chiaSe.kenhLo && d.chiaSe.chiaTheoThucNhan && (
                    <p className="text-phu text-warn-chu">Kênh lỗ — chia theo thực nhận</p>
                  )}
                </div>
              ) : null,
          },
          ...(xemChiaDayDu
            ? ([
                {
                  key: 'chiaNhanSu',
                  header: 'Chia nhân sự',
                  align: 'right',
                  render: (d) =>
                    d.chiaSe ? (
                      <div>
                        <OTienChia giaTri={d.chiaSe.chiaNhanSu} />
                        {d.chiaSe.canhBaoVuotPct && (
                          <p className="so-lieu text-phu text-warn-chu">
                            tổng{' '}
                            {(d.chiaSe.tongPct ?? 0).toLocaleString('vi-VN', {
                              maximumFractionDigits: 2,
                            })}
                            %
                          </p>
                        )}
                      </div>
                    ) : null,
                },
                {
                  key: 'congTyGiu',
                  header: 'Công ty giữ lại',
                  align: 'right',
                  render: (d) => (d.chiaSe ? <OTienChia giaTri={d.chiaSe.congTyGiu} am /> : null),
                },
              ] satisfies CotBang<ChannelRow>[])
            : ([
                {
                  key: 'phanCuaToi',
                  header: 'Phần của bạn',
                  align: 'right',
                  render: (d) => (d.chiaSe ? <OTienChia giaTri={d.chiaSe.phanCuaToi} /> : null),
                },
              ] satisfies CotBang<ChannelRow>[])),
        ] satisfies CotBang<ChannelRow>[])),
    {
      key: 'truongNhom',
      header: 'Trưởng nhóm',
      render: (d) => <PillNguoi ds={d.chiaSe?.truongNhom ?? []} />,
    },
    { key: 'quanLy', header: 'Quản lý', render: (d) => <PillNguoi ds={d.chiaSe?.quanLy ?? []} /> },
    { key: 'editor', header: 'Editor', render: (d) => <PillNguoi ds={d.chiaSe?.editor ?? []} /> },
  ];

  /*
   * Ba cột pill trên chỉ có tên **người đang xem** khi họ xem hẹp (máy chủ lọc,
   * xem `chiaSe.chiaSeTheoLo`), nên bảng vẫn đọc được; đây chỉ là dòng giải
   * thích để không ai tưởng đội ngũ công ty chỉ có một người.
   */

  const cot = cachXem === 'danh-sach' ? cotDanhSach : cotChiaTien;

  /**
   * Gom dòng cho hai cách xem có tiêu đề nhóm.
   *
   * Tổng con cộng từ **các dòng đã tải**; bấm "Tải thêm" thì tổng con tăng
   * theo. Nói rõ ở chú thích dưới bảng để không ai đọc nhầm thành tổng cả kỳ.
   */
  const nhomHienThi = useMemo(() => {
    if (cachXem === 'danh-sach') return [];

    const gom = new Map<string, { ten: string; rows: ChannelRow[] }>();

    if (cachXem === 'theo-nhom') {
      gom.set(KHONG_NHOM, { ten: 'Chưa gán nhóm', rows: [] });
      for (const d of dongs) {
        const khoa = d.chiaSe?.teamId ?? KHONG_NHOM;
        const ten = d.chiaSe?.tenNhom ?? 'Chưa gán nhóm';
        const o = gom.get(khoa) ?? { ten, rows: [] };
        o.rows.push(d);
        gom.set(khoa, o);
      }
    } else {
      for (const d of dongs) {
        const nguoi = [
          ...(d.chiaSe?.truongNhom ?? []),
          ...(d.chiaSe?.quanLy ?? []),
          ...(d.chiaSe?.editor ?? []),
        ];
        if (nguoi.length === 0) {
          const o = gom.get(KHONG_NHOM) ?? { ten: 'Chưa có đội ngũ', rows: [] };
          o.rows.push(d);
          gom.set(KHONG_NHOM, o);
          continue;
        }
        // Một kênh xuất hiện dưới **mỗi** người ăn chia của nó.
        for (const n of new Map(nguoi.map((x) => [x.userId, x])).values()) {
          const o = gom.get(n.userId) ?? { ten: n.tenNguoi, rows: [] };
          o.rows.push(d);
          gom.set(n.userId, o);
        }
      }
    }

    return [...gom].map(([khoa, o]) => {
      const cong = (lay: (d: ChannelRow) => number) => o.rows.reduce((t, d) => t + lay(d), 0);

      return {
        khoa,
        ten: o.ten,
        rows: o.rows,
        tongCon: {
          kenh: `${String(o.rows.length)} kênh`,
          ...(!coXemTien
            ? {}
            : {
                thucNhan: <Money value={cong((d) => d.chiaSe?.thucNhan ?? 0)} />,
                chiPhi: <Money value={cong((d) => d.chiaSe?.chiPhi ?? 0)} />,
                loiNhuan: <Money value={cong((d) => d.chiaSe?.loiNhuan ?? 0)} />,
                ...(xemChiaDayDu
                  ? {
                      chiaNhanSu: <Money value={cong((d) => d.chiaSe?.chiaNhanSu ?? 0)} />,
                      congTyGiu: <Money value={cong((d) => d.chiaSe?.congTyGiu ?? 0)} />,
                    }
                  : { phanCuaToi: <Money value={cong((d) => d.chiaSe?.phanCuaToi ?? 0)} /> }),
              }),
        },
      };
    });
  }, [cachXem, dongs]);

  const tongKet = tong
    ? {
        kenh: `${String(tong.channels)} kênh`,
        subs: gonSo(tong.subscribers),
        views: gonSo(tong.views),
        /* Ba ô tổng tiền — mục S5: người không có `revenue:view` không nhận ba
           trường này, và bảng của họ cũng không có ba cột đó. */
        ...(!coXemTien
          ? {}
          : {
              gop: <Money value={tong.grossRevenue} />,
              thucNhan: <Money value={tong.companyNet} />,
              rpm:
                tong.avgRpm === null || tong.avgRpm === undefined ? (
                  <span className="text-chu-mo" title={CHUA_DU_LUOT_XEM}>
                    —
                  </span>
                ) : (
                  <Money value={tong.avgRpm} />
                ),
            }),
      }
    : undefined;

  /*
   * Bốn ô đầu của dải chỉ số đều là tiền (mục S5). Người không có
   * `revenue:view` thấy dải rỗng — **không** thấy bốn ô `—`, vì một ô `—`
   * mang tên "Doanh thu gộp" vẫn nói ra ở đây có một con số tiền.
   */
  const o: OChiSo[] = !coXemTien
    ? []
    : [
        {
          nhan: `Doanh thu gộp · ${nhanKy}`,
          giaTri: <Money value={tomTat.data?.totals.grossRevenue ?? null} />,
          ...(tomTat.data?.grossDeltaPercent != null
            ? {
                delta: {
                  huong: tomTat.data.grossDeltaPercent >= 0 ? ('tang' as const) : ('giam' as const),
                  text: `${phanTram(Math.abs(tomTat.data.grossDeltaPercent))} ${nhanSoKyTruoc(kyTrang)}`,
                },
              }
            : {}),
        },
        {
          nhan: `Phần chia Network · ${nhanKy}`,
          giaTri: <Money value={tomTat.data?.totals.networkShare ?? null} />,
          ...(tomTatNetwork.data
            ? { phu: `${String(tomTatNetwork.data.payingPartners)} network đang trả tiền` }
            : {}),
        },
        {
          nhan: `Công ty thực nhận · ${nhanKy}`,
          giaTri: <Money value={tomTat.data?.totals.companyNet ?? null} />,
          ...(tomTat.data?.netPercent === undefined
            ? {}
            : { phu: `${phanTram(tomTat.data.netPercent)} doanh thu gộp` }),
        },
        {
          nhan: `RPM trung bình · ${nhanKy}`,
          giaTri:
            tomTat.data && tomTat.data.totals.avgRpm === null ? (
              <span className="text-chu-mo" title={CHUA_DU_LUOT_XEM}>
                —
              </span>
            ) : (
              <Money value={tomTat.data?.totals.avgRpm ?? null} />
            ),
          ...(tomTat.data?.totals.avgRpm === null
            ? { phu: 'Chưa đủ số liệu lượt xem' }
            : tomTat.data?.topRpm
              ? {
                  phu: `cao nhất: ${tomTat.data.topRpm.name} ${dinhDangTien(tomTat.data.topRpm.rpm)}`,
                }
              : {}),
        },
      ];

  return (
    <>
      <PageHeader
        title="Quản lý kênh"
        phu={
          tomTat.data
            ? `${String(tomTat.data.counts.all)} kênh đang hoạt động · ${tomTat.data.nhanKy} · ${nhanKhoangNgay(tomTat.data.from, tomTat.data.to)}`
            : undefined
        }
        giaiThich="Hồ sơ, doanh thu, Network thanh toán và đội ngũ của từng kênh. Kỳ số liệu chọn ở ô kỳ trên thanh trên."
        actions={
          <>
            {coDongBo && (
              <Nut
                kieu="phu"
                dangChay={dongBoNgay.isPending}
                onClick={() => {
                  dongBoNgay.mutate(undefined);
                }}
              >
                Đồng bộ YouTube
              </Nut>
            )}
            {coSua && (
              <Link
                to="/kenh/moi"
                className="vung-bam inline-flex items-center justify-center rounded-o bg-nhan px-4 text-co-ban font-semibold text-white hover:bg-nhan-dam"
              >
                + Thêm kênh
              </Link>
            )}
          </>
        }
      />

      <KhoiChuaCoKenh tenTrang="Quản lý kênh" />

      <div className="mb-4">
        <StatStrip
          aria-label={`Chỉ số ${nhanKy}`}
          o={o}
          /* Kỳ ngoài mốc dữ liệu (link cũ `?ky=2017`): API trả 0, mà `0,00 $` đọc
           ra là "tụt về không". Hiện `—`; dòng "Không có số liệu trong kỳ này"
           nằm cạnh ô chọn kỳ (`ui/ChonKy`). */
          kyRong={kyRong}
        />
      </div>

      {/* Dẫn sang màn Kết nối API, theo mô tả ở prompt bước 12 mục 2. */}
      {tomTatSync.data && (
        <p className="mb-3 text-phu text-chu-phu">
          API đã kết nối {tomTatSync.data.connectedChannels}/{tomTatSync.data.totalChannels} kênh
          {tomTatSync.data.expiredTokens > 0 && (
            <span className="text-bad-chu">
              {' · '}
              {tomTatSync.data.expiredTokens} token hết hạn
            </span>
          )}{' '}
          <Link
            to="/cai-dat?tab=ket-noi-api"
            className="font-semibold text-nhan hover:text-nhan-dam"
          >
            →
          </Link>
        </p>
      )}

      <ToolbarBang
        trai={
          <SegmentedControl
            aria-label="Nhóm kênh theo nguồn tiền"
            giaTri={tab}
            onChange={datTab}
            muc={CHANNEL_TABS.map((t) => ({
              giaTri: t,
              nhan: CHANNEL_TAB_LABEL[t],
              ...(tomTat.data ? { dem: tomTat.data.counts[t] } : {}),
            }))}
          />
        }
        phai={
          <>
            <OTimKiem value={q} onChange={setQ} placeholder="Tìm kênh…" label="Tìm kênh" />
            <OChon
              label="Nhóm"
              value={nhomLoc}
              onChange={setNhomLoc}
              options={[
                { value: '', label: 'Mọi nhóm' },
                { value: KHONG_NHOM, label: 'Chưa gán nhóm' },
                ...nhomChon,
              ]}
            />
            <OChon
              label="Network"
              value={partner}
              onChange={setPartner}
              options={[{ value: '', label: 'Mọi Network' }, ...doiTac]}
            />
            <OChon
              label="Người phụ trách"
              value={nguoiLoc}
              onChange={setNguoiLoc}
              options={[{ value: '', label: 'Mọi người' }, ...nguoiChon]}
            />
            <OChon
              label="Sắp xếp"
              value={sort}
              onChange={(v) => {
                setSort(v as ChannelSort);
              }}
              options={cachSapXemDuoc(coXemTien).map((s) => ({
                value: s,
                label: CHANNEL_SORT_LABEL[s],
              }))}
            />
            {/*
             * Bốn ô lọc ít dùng nằm sau một nút: thanh công cụ phải cao **một
             * hàng**, mà chín ô lọc thì luôn thành ba hàng ở khổ 1440.
             */}
            <Nut
              kieu="phu"
              aria-expanded={moLocThem}
              onClick={() => {
                setMoLocThem((x) => !x);
              }}
            >
              {moLocThem ? 'Ẩn bộ lọc khác' : 'Bộ lọc khác'}
            </Nut>
          </>
        }
      />

      {moLocThem && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-the border border-vien bg-be-mat px-3 py-2.5">
          <OChon
            label="Cách xem"
            hienNhan
            value={cachXem}
            onChange={(v) => {
              setCachXem(v as CachXem);
            }}
            options={CACH_XEM.map((c) => ({ value: c.value, label: c.label }))}
          />
          <OChon
            label="Kiếm tiền"
            hienNhan
            value={kiemTien}
            onChange={setKiemTien}
            options={[
              { value: '', label: 'Mọi tình trạng' },
              ...MONETIZATION_STATUSES.map((m) => ({ value: m, label: MONETIZATION_LABEL[m] })),
            ]}
          />
          <OChon
            label="Vai trò"
            hienNhan
            value={vaiTroLoc}
            onChange={setVaiTroLoc}
            options={[
              { value: '', label: 'Mọi vai trò' },
              ...(['truong_nhom', 'quan_ly', 'editor'] as VaiTroChia[]).map((v) => ({
                value: v,
                label: VAI_TRO_CHIA_LABEL[v],
              })),
            ]}
          />
          <OTimKiem
            value={emailQuyen}
            onChange={datEmailQuyen}
            placeholder="email có quyền…"
            label="Quyền thương hiệu"
          />
          <p className="text-phu text-chu-phu">
            Vai trò chỉ có nghĩa khi đã chọn một người. Ô email tìm trong chủ sở hữu và người quản
            lý Tài khoản thương hiệu.
          </p>
        </div>
      )}

      {danhSach.error ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được danh sách kênh"
            description={danhSach.error.message}
            action={
              <Nut
                kieu="phu"
                onClick={() => {
                  void danhSach.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      ) : cachXem === 'danh-sach' ? (
        <DataTable
          caption="Danh sách kênh YouTube của WM Media"
          columns={cot}
          rows={dongs}
          keyOf={(d) => d.id}
          dangTai={danhSach.isPending}
          {...(tongKet ? { tongKet } : {})}
          {...(tong ? { tongSo: tong.channels } : {})}
          conNua={danhSach.hasNextPage}
          dangTaiThem={danhSach.isFetchingNextPage}
          onTaiThem={() => {
            void danhSach.fetchNextPage();
          }}
          khiRong={
            <EmptyState
              title="Chưa có kênh nào khớp bộ lọc"
              description="Thử xoá bớt bộ lọc hoặc thêm kênh mới."
              action={
                coSua ? (
                  <Link
                    to="/kenh/moi"
                    className="vung-bam inline-flex items-center justify-center rounded-o bg-nhan px-4 text-co-ban font-semibold text-white hover:bg-nhan-dam"
                  >
                    + Thêm kênh
                  </Link>
                ) : undefined
              }
            />
          }
        />
      ) : (
        <div className="space-y-5">
          {nhomHienThi.map((g) => (
            <section key={g.khoa} aria-label={g.ten}>
              <h3 className="mb-2 text-tieu-de-khoi font-semibold text-chu">
                {g.ten}
                <span className="ml-2 text-phu font-normal text-chu-phu">{g.rows.length} kênh</span>
              </h3>
              <DataTable
                caption={`Kênh thuộc ${g.ten}`}
                columns={cot}
                rows={g.rows}
                keyOf={(d) => d.id}
                dangTai={danhSach.isPending}
                tongKet={g.tongCon}
                khiRong={
                  <EmptyState
                    title="Chưa có kênh nào"
                    description={
                      g.khoa === KHONG_NHOM
                        ? 'Mọi kênh đang xem đều đã thuộc một nhóm.'
                        : 'Nhóm này chưa có kênh nào khớp bộ lọc.'
                    }
                  />
                }
              />
            </section>
          ))}

          <p className="text-phu text-chu-phu">
            Tổng con cộng từ các dòng đã tải. Còn kênh chưa tải thì bấm “Tải thêm”.
          </p>

          {danhSach.hasNextPage && (
            <Nut
              kieu="phu"
              dangChay={danhSach.isFetchingNextPage}
              onClick={() => {
                void danhSach.fetchNextPage();
              }}
            >
              Tải thêm
            </Nut>
          )}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 @min-[900px]/noi-dung:grid-cols-2">
        <BieuDoTyTrong duLieu={tomTat.data?.revenueShare ?? []} />
        <TheKenhChuaCoDoanhThu duLieu={tomTat.data?.channelsWithoutRevenue ?? []} />
      </div>
    </>
  );
}
